import React, { useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { DEFAULT_TIMEZONE } from 'Consts/timezone';
import {
  clearCourseForEditError,
  clearUpdateCourseResult,
  getCourseForEdit,
  updateCourse,
  useSelectCourse,
  useSelectCourseForEditError,
  useSelectIsCourseForEditLoading,
  useSelectIsCourseUpdating,
  useSelectIsUpdateCourseSuccess
} from 'Courses/data';
import { useAppDispatch } from 'Store';
import {
  convertDateFormatToDayjs,
  convertTimeFormatToDayjs,
  convertUtcToTimezone,
  formatDayjsToString,
  isValidDayjs
} from 'Utils/dateTime';

import { useDateTimeConfigFromConfigSlice } from '@hooks/useDateTimeConfigFromConfigSlice';
import type { IUpdateCourseRequest } from '@libs/models';

import { convertCourseDatesToUtcFormat } from '../../dates/course-dates-utils';

import type { CourseFormData } from '../shared/types';

import { EditCourseModal } from './edit-course-modal';

type Props = {
  open: boolean;
  onClose: () => void;
  courseId: string;
};

export const EditCourseFormContainer = ({ open, onClose, courseId }: Readonly<Props>) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { dateFormat, timeFormat } = useDateTimeConfigFromConfigSlice();

  const course = useSelectCourse(courseId);
  const isUpdateCourseLoading = useSelectIsCourseUpdating();
  const isUpdateCourseSuccess = useSelectIsUpdateCourseSuccess();
  const isCourseForEditLoading = useSelectIsCourseForEditLoading();
  const courseForEditError = useSelectCourseForEditError();

  useEffect(() => {
    if (open && courseId) {
      dispatch(clearCourseForEditError());
      dispatch(getCourseForEdit(courseId));
    }
  }, [open, courseId, dispatch]);

  useEffect(() => {
    if (!open) {
      dispatch(clearUpdateCourseResult());
      dispatch(clearCourseForEditError());
    }
  }, [open, dispatch]);

  const handleSubmit = async (submissionData: CourseFormData) => {
    try {
      const request: IUpdateCourseRequest = {
        courseId,
        title: submissionData.title,
        description: submissionData.description ?? '',
        timezone: submissionData.timezone ?? DEFAULT_TIMEZONE,
        courseDates: [],
        maxParticipants: submissionData.maxParticipants ?? 20,
        expectedParticipants: submissionData.expectedParticipants ?? 0,
        daDatacenterIds: submissionData.daDataCenterIds ?? [],
        wantToBeCoordinator: submissionData.wantToBeCoordinator ?? false
      };

      if (submissionData.courseDates && submissionData.courseDates.length > 0) {
        const utcCourseDates = convertCourseDatesToUtcFormat(
          submissionData.courseDates,
          submissionData.timezone,
          dateFormat,
          timeFormat
        );
        request.courseDates = utcCourseDates.map(dateEntry => ({
          startDate: dateEntry.startDate,
          endDate: dateEntry.endDate
        }));
      }

      await dispatch(updateCourse(request)).unwrap();
    } catch (error) {
      toast.error(t('courseForm.updateError', 'Failed to update course. Please try again.'));

      throw error;
    }
  };

  const handleClose = () => {
    dispatch(clearUpdateCourseResult());
    onClose();
  };

  useEffect(() => {
    if (isUpdateCourseSuccess) {
      dispatch(getCourseForEdit(courseId));

      toast.success(t('courseForm.courseUpdatedSuccessfully', 'Course updated successfully!'));

      handleClose();
    }
  }, [isUpdateCourseSuccess, t, dispatch, handleClose, courseId]);

  const getInitialFormData = (): Partial<CourseFormData> | undefined => {
    if (!course) {
      return undefined;
    }

    const courseDates =
      course.dates?.map((date, index) => {
        try {
          const startDate =
            typeof date.startDate === 'string' ? date.startDate : date.startDate.toISOString();
          const endDate =
            typeof date.endDate === 'string' ? date.endDate : date.endDate.toISOString();

          const courseTimezone = course.timezone ?? DEFAULT_TIMEZONE;

          const startInCourseTz = convertUtcToTimezone(startDate, courseTimezone);
          const endInCourseTz = convertUtcToTimezone(endDate, courseTimezone);

          if (!isValidDayjs(startInCourseTz) || !isValidDayjs(endInCourseTz)) {
            return {
              id: `temp-${index}`,
              startDate: '',
              endDate: '',
              startTime: '',
              endTime: ''
            };
          }

          const dayjsDateFormat = convertDateFormatToDayjs(dateFormat);
          const dayjsTimeFormat = convertTimeFormatToDayjs(timeFormat);

          return {
            id: `temp-${index}`,
            startDate: formatDayjsToString(startInCourseTz, dayjsDateFormat),
            endDate: formatDayjsToString(endInCourseTz, dayjsDateFormat),
            startTime: formatDayjsToString(startInCourseTz, dayjsTimeFormat),
            endTime: formatDayjsToString(endInCourseTz, dayjsTimeFormat)
          };
        } catch {
          return {
            id: `temp-${index}`,
            startDate: '',
            endDate: '',
            startTime: '',
            endTime: ''
          };
        }
      }) ?? [];

    return {
      title: course.title,
      description: course.description ?? '',
      timezone: course.timezone ?? DEFAULT_TIMEZONE,
      maxParticipants: course.maxParticipants ?? 20,
      expectedParticipants: course.expectedParticipants ?? 10,
      daDataCenterIds: course.dataCenterIds ?? [],
      daOrganizationId: course.organizationId ?? 1,
      courseDates,
      wantToBeCoordinator: course.wantToBeCoordinator ?? course.flags?.isCoordinator ?? false,
      isDemo: course.isDemo ?? false,
      wantToBeTrainer: course.wantToBeTrainer ?? false
    };
  };

  if (!open) {
    return null;
  }

  if (courseForEditError) {
    return (
      <EditCourseModal
        open={open}
        onClose={handleClose}
        initialData={undefined}
        onSubmit={handleSubmit}
        isLoading={false}
        isSuccess={false}
        error={courseForEditError}
        onRetry={() => dispatch(getCourseForEdit(courseId))}
      />
    );
  }

  return (
    <EditCourseModal
      open={open}
      onClose={handleClose}
      initialData={getInitialFormData()}
      onSubmit={handleSubmit}
      isLoading={isUpdateCourseLoading || isCourseForEditLoading}
      isSuccess={isUpdateCourseSuccess}
    />
  );
};
